/**
 * Cross-cutting "your session is no longer valid" signal.
 *
 * Any authenticated request that comes back 401 dispatches this so the auth
 * layer (useAuth) can confirm against /auth/me and, if the session is really
 * gone, flip `sessionExpired` — which routes the user to the calm "session
 * timed out" re-auth surface (ProtectedRoute → AuthSurface variant="expired")
 * instead of leaving them inside the app with every data fetch erroring (the
 * stuck "Couldn't load your projects" state).
 *
 * Decoupled via a window CustomEvent so the fetch/query layer (lib/queryClient)
 * doesn't import React/auth state and useAuth doesn't import the query layer —
 * either side can dispatch or subscribe without a dependency cycle.
 */
export const UNAUTHORIZED_EVENT = 'anaka:unauthorized';

/**
 * True only for an error that represents an HTTP 401 — never a 403 (authenticated
 * but forbidden, e.g. a stale org id), 404, or 5xx. We must be strict: flipping
 * the session-expired state on a 403/5xx would log out a user whose session is
 * actually fine.
 *
 * Handles both shapes the codebase throws:
 *   - a typed error carrying `.status` (useProjects, useProjectByShortId, …)
 *   - a bare `Error` whose message embeds the status (`"Bets fetch failed: 401"`,
 *     `"401: Unauthorized"`) — used by hooks that don't attach `.status`.
 */
export function isUnauthorizedError(error: unknown): boolean {
  if (!error) return false;
  const status = (error as { status?: number }).status;
  if (status === 401) return true;
  // A typed non-401 status is authoritative — do not fall back to message sniffing.
  if (typeof status === 'number') return false;
  const message = error instanceof Error ? error.message : String(error);
  return /(?:^|\D)401(?:\D|$)/.test(message) || /unauthorized/i.test(message);
}

/** Broadcast that an authenticated request was rejected with 401. */
export function notifyUnauthorized(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
}
